/**
 * Every tunable number of the deterministic simulation lives in this module.
 *
 * Units: blocks (1 block = the cube's size), seconds, ticks. y grows UP. Velocities
 * are blocks/s, accelerations blocks/s². "Relative up" means away from the player's
 * feet, i.e. world-up for normal gravity and world-down for reversed gravity.
 *
 * Changing any value here changes how every generated level plays: the generator's
 * solver uses the same numbers, so new levels stay beatable, but the golden race
 * snapshot in the tests must be updated on purpose.
 */
import type { Mode } from '../level/types';

/** The simulation runs at a fixed rate; every deterministic quantity is counted in ticks. */
export const TICK_RATE = 120;
export const DT = 1 / TICK_RATE;
export const TICK_MS = 1000 / TICK_RATE;

/** Horizontal speed in blocks per second for each SpeedKey: slow, normal, fast, very fast. */
export const SPEEDS = [8.4, 10.4, 12.96, 15.6] as const;

// ---------------------------------------------------------------------------
// Player boxes
// ---------------------------------------------------------------------------

export interface BoxSize {
  readonly w: number;
  readonly h: number;
}

/** The three boxes of a movement mode, all centred on the player's (x, y). */
export interface ModeBoxes {
  /** Landing, head bumps, pads, portals and pickups. This is also the size the client draws. */
  readonly outer: BoxSize;
  /** Overlapping any solid with this box is a crash. Small, so clipping a corner is forgiven. */
  readonly inner: BoxSize;
  /** Tested against the (already forgiving) spike hitboxes. */
  readonly hazard: BoxSize;
}

const STANDARD_BOXES: ModeBoxes = {
  outer: { w: 1, h: 1 },
  inner: { w: 0.4, h: 0.4 },
  hazard: { w: 0.8, h: 0.8 },
};

export const MODE_BOXES: Readonly<Record<Mode, ModeBoxes>> = {
  cube: STANDARD_BOXES,
  ship: STANDARD_BOXES,
  ball: STANDARD_BOXES,
  ufo: STANDARD_BOXES,
  robot: STANDARD_BOXES,
  spider: STANDARD_BOXES,
  // The wave is small and fragile: its hazard box is not shrunk at all.
  wave: {
    outer: { w: 0.5, h: 0.5 },
    inner: { w: 0.24, h: 0.24 },
    hazard: { w: 0.5, h: 0.5 },
  },
};

// ---------------------------------------------------------------------------
// Movement modes
// ---------------------------------------------------------------------------

/**
 * Cube: jumps from the ground. The discrete (semi-implicit Euler) jump peaks at
 * ~2.1 blocks and lasts ~0.45 s, i.e. ~4.7 blocks at normal speed.
 */
export const CUBE = {
  gravity: 83,
  jumpVelocity: 19,
  /** Also caps the per-tick fall below LAND_TOLERANCE, so landings are never skipped. */
  maxFall: 26,
} as const;

/** Ship: holding accelerates relative-up, releasing falls. Slides along floors and ceilings. */
export const SHIP = {
  lift: 55,
  gravity: 45,
  maxSpeed: 9,
} as const;

/** Ball: a press while grounded flips gravity with a small kick toward the new floor. */
export const BALL = {
  gravity: 75,
  maxFall: 26,
  flipKick: 5,
} as const;

/** UFO: every press sets a fixed relative-up velocity, also in the air. */
export const UFO = {
  gravity: 60,
  maxFall: 14,
  flapVelocity: 12,
} as const;

/** Wave: vertical speed is always ±slope × horizontal speed (45° zig-zag). */
export const WAVE = {
  slope: 1,
} as const;

/**
 * Robot: jumps from the ground like the cube, but its jump is a thrust: it rises at
 * `jumpVelocity` for as long as the button is held (at least `minThrustTicks`, at most
 * `maxThrustTicks`), then falls under its gravity. A quick tap (any press up to minThrustTicks,
 * 100 ms, about how long a child's tap lasts) is a hop of ~1.45 blocks over ~3.9 blocks of run;
 * held to the end of the thrust (250 ms) it jumps ~3 blocks high over ~6 blocks; every tick held
 * in between adds ~0.08 block. Letting go ends the thrust for good (no hovering), and so do a
 * head bump, a pad, an orb or a gravity change. Unlike the cube, a hold that lasts past the
 * landing does not jump again: every jump is a press of its own.
 */
export const ROBOT = {
  gravity: 100,
  jumpVelocity: 10,
  minThrustTicks: 12,
  maxThrustTicks: 30,
  maxFall: 26,
} as const;

/**
 * Spider: runs on its surface; a press teleports it straight to the nearest surface on the
 * other side (floor ↔ ceiling) and flips its gravity, all within the tick. It falls like the cube
 * when it walks off a ledge or passes a gravity portal.
 */
export const SPIDER = {
  gravity: 83,
  maxFall: 26,
} as const;

// ---------------------------------------------------------------------------
// Interactive objects
// ---------------------------------------------------------------------------

/** Relative-up launch speed of jump pads (blue pads flip gravity instead). Peaks: ~4 / ~1.3 blocks. */
export const PAD_VELOCITY = {
  yellow: 26,
  pink: 15,
} as const;

/** Relative-up velocity given by orbs (green: applied after flipping gravity). */
export const ORB_VELOCITY = {
  yellow: 19,
  pink: 14,
  green: 19,
} as const;

/** Speed toward the new floor after a blue pad / blue orb flips gravity. */
export const GRAVITY_FLIP_KICK = 8;

/** Gravity portals keep this fraction of the vertical speed (softens the turn-around). */
export const GRAVITY_PORTAL_DAMPING = 0.5;

/** Orbs react this far outside their cell, on every side. */
export const ORB_REACH = 0.2;

/**
 * Spike hitbox as fractions of its cell: a central strip `width` wide (across the
 * base) and `height` tall (from the base toward the tip). Much smaller than the
 * drawn triangle, so near misses never feel unfair.
 */
export const SPIKE_HITBOX = {
  width: 0.3,
  height: 0.45,
} as const;

/**
 * Saw blades kill within this share of their radius (half the cell): the teeth and the rim
 * around the hub are forgiven, as a spike's edges are.
 */
export const SAW_HITBOX = 0.7;

/**
 * Rotors' deadly shapes (see RotorObject), from the arm's reach (half the cell) in. A spiked
 * bar kills within `barRadius` of its axis — tested as circles `barStep` apart, the outermost
 * `barInset` inside the reach (its tip spike is forgiven), down to the pivot. A mace's ball is
 * drawn with its spikes' tips at the reach, its centre `maceInset` inside it; it kills within
 * `maceRadius` of that centre (its chain does not).
 */
export const ROTOR = {
  barRadius: 0.25,
  barStep: 0.3,
  barInset: 0.3,
  maceInset: 0.8,
  maceRadius: 0.5,
} as const;

// ---------------------------------------------------------------------------
// Collision & input forgiveness
// ---------------------------------------------------------------------------

/**
 * A landing (or head bump) is accepted when, on the previous tick, the feet (head)
 * were at most this far past the surface. Larger than any per-tick movement.
 */
export const LAND_TOLERANCE = 0.25;

/** Ticks after walking off a ledge during which a cube, ball, robot or spider can still act. */
export const COYOTE_TICKS = 4;

/** A press stays usable this long for actions that need ground contact (jump/flip on landing). */
export const PRESS_BUFFER_TICKS = 12;

/** A press stays usable this long for orbs (pressing a moment before touching one still works). */
export const ORB_BUFFER_TICKS = 6;

/**
 * Upper bound of the solver's vertical safety margin per mode (see CollisionProfile).
 * It only applies where height is continuously controlled, and stays below the gap
 * between the outer and inner box so sliding along a floor is never "unsafe".
 */
export const MARGIN_Y_CAP: Readonly<Record<Mode, number>> = {
  cube: 0,
  ball: 0,
  ship: 0.25,
  ufo: 0.25,
  wave: 0.12,
  robot: 0,
  spider: 0,
};

// ---------------------------------------------------------------------------
// World limits
// ---------------------------------------------------------------------------

/** Falling below this y is a pit death (the base ground's top is y = 0). */
export const WORLD_BOTTOM_Y = -4;
/** Rising above this y (reversed gravity without a ceiling) is also a pit death. */
export const WORLD_TOP_Y = 16;
/** Speed an invulnerable player is bounced back with from WORLD_BOTTOM_Y / WORLD_TOP_Y. */
export const PIT_BOUNCE_VELOCITY = 30;

// ---------------------------------------------------------------------------
// Player simulation
// ---------------------------------------------------------------------------

/** Ticks between a death and the restart of the level (input is ignored meanwhile). */
export const RESPAWN_DELAY_TICKS = 72;
/** Ticks between completing a level and starting the next one. */
export const LEVEL_TRANSITION_TICKS = 60;
